// Composables
import { createRouter, createWebHistory } from "vue-router";
import { getCurrentUser } from "vuefire";
import { useAlertStore } from "@/store/alert";
import {
  ADMIN_ACCESS_CACHE_TTL_MS,
  useAdminAccessStore,
} from "@/store/adminAccess";
import Home from "@/views/Home.vue";
import Login from "@/views/Login.vue";
import SetupAccount from "@/views/SetupAccount.vue";
import Verify from "@/views/Verify.vue";
import PaymentSuccess from "@/views/PaymentSuccess.vue";
import Onboarding from "@/views/Onboarding.vue";
import ChangePassword from "@/views/ChangePassword.vue";
import Help from "@/views/Help.vue";
import Profile from "@/views/Profile.vue";
import Sessions from "@/views/Sessions.vue";
import NewSession from "@/views/NewSession.vue";
import NewInterviewSession from "@/views/NewInterviewSession.vue";
import Session from "@/views/Session.vue";
import Terms from "@/views/Terms.vue";
import Privacy from "@/views/Privacy.vue";
import { installRouteProfiler } from "@/utils/routeProfiler.mjs";
import { installPerformanceTelemetry } from "@/utils/performanceTelemetry.mjs";
import { functions } from "@/plugins/firebase";
const routes = [
  {
    path: "/",
    component: () => import("@/layouts/default/Default.vue"),
    children: [
      {
        path: "",
        name: "Home",
        component: Home,
      },
      {
        path: "/login",
        name: "Login",
        component: Login,
      },
      {
        path: "/setup-account",
        name: "SetupAccount",
        component: SetupAccount,
      },
      {
        path: "/paymentsuccess",
        name: "PaymentSuccess",
        component: PaymentSuccess,
      },
      {
        path: "/verify",
        name: "Verify",
        component: Verify,
      },
      {
        path: "/onboarding",
        name: "Onboarding",
        component: Onboarding,
      },
      {
        path: "/change-password",
        name: "ChangePassword",
        component: ChangePassword,
      },
      {
        path: "/help",
        name: "Help Page",
        component: Help,
      },
      {
        path: "/profile",
        name: "Profile Page",
        component: Profile,
      },
      {
        path: "/portfolio",
        name: "ST1 Portfolio",
        component: () =>
          import(/* webpackChunkName: "Portfolio" */ "@/views/Portfolio.vue"),
      },
      {
        path: "/pub2",
        component: () =>
          import(/* webpackChunkName: "Pub2" */ "@/views/pub2/Pub2Layout.vue"),
        children: [
          {
            path: "",
            name: "Pub2 Dashboard",
            component: () =>
              import(/* webpackChunkName: "Pub2" */ "@/views/pub2/Pub2Dashboard.vue"),
          },
          {
            path: "modules",
            name: "Pub2 Modules",
            component: () =>
              import(/* webpackChunkName: "Pub2" */ "@/views/pub2/Pub2Modules.vue"),
          },
          {
            path: "modules/:moduleId",
            name: "Pub2 Module",
            component: () =>
              import(/* webpackChunkName: "Pub2" */ "@/views/pub2/Pub2Module.vue"),
          },
          {
            path: "group",
            name: "Pub2 Group",
            component: () =>
              import(/* webpackChunkName: "Pub2" */ "@/views/pub2/Pub2Group.vue"),
          },
          {
            path: "conferences",
            name: "Pub2 Conferences",
            component: () =>
              import(/* webpackChunkName: "Pub2" */ "@/views/pub2/Pub2Conferences.vue"),
          },
          {
            path: "supervisor",
            name: "Pub2 Supervisor",
            component: () =>
              import(/* webpackChunkName: "Pub2" */ "@/views/pub2/Pub2Supervisor.vue"),
          },
        ],
      },
      {
        path: "/sessions",
        name: "Sessions Page",
        component: Sessions,
      },
      {
        path: "/sessions/interview-bank/new",
        name: "New Interview Session",
        component: NewInterviewSession,
      },
      {
        path: "/sessions/part-2-video-bank/new",
        name: "New Part 2 Oral Session",
        component: NewInterviewSession,
      },
      {
        path: "/sessions/interview-bank/:id",
        name: "InterviewSessionPage",
        component: () =>
          import(
            /* webpackChunkName: "InterviewSessionPage" */ "@/views/SessionInterview.vue"
          ),
      },
      {
        path: "/sessions/part-2-video-bank/:id",
        name: "InterviewVideoSessionPage",
        component: () =>
          import(
            /* webpackChunkName: "InterviewVideoSessionPage" */ "@/views/SessionVideoBank.vue"
          ),
      },
      // {
      //   path: "/sessions/part-2-video-bank/:id",
      //   name: "InterviewSessionPage",
      //   component: () =>
      //     import(
      //       /* webpackChunkName: "InterviewSessionPage" */ "@/views/SessionInterview.vue"
      //     ),
      // },
      {
        path: "/sessions/new",
        name: "New Session",
        component: NewSession,
      },
      {
        path: "/sessions/part-1-question-bank/new",
        name: "New Session Part 1",
        component: NewSession,
      },
      {
        path: "/sessions/duke-elder-question-bank/new",
        name: "New Session Duke Elder",
        component: NewSession,
      },
      {
        path: "/sessions/:bank/:id",
        name: "Part 1 & Duke Elder Session Page",
        component: Session,
      },
      {
        path: "/sessions/:id",
        name: "Session Page",
        component: Session,
      },

      {
        path: "/comments/interview-bank/",
        name: "InterviewStationCommentsPage",
        component: () =>
          import(
            /* webpackChunkName: "InterviewStationCommentsPage" */ "@/views/CollatedComments.vue"
          ),
      },
      {
        path: "/comments/part-2-video-bank/",
        name: "VideoStationCommentsPage",
        component: () =>
          import(
            /* webpackChunkName: "VideoStationCommentsPage" */ "@/views/CollatedComments.vue"
          ),
      },
      {
        // Special bank questions route
        path: "/stations/interview-bank/:id",
        name: "InterviewStationPage",
        component: () =>
          import(
            /* webpackChunkName: "InterviewStationPage" */ "@/views/SessionInterview.vue"
          ),
      },
      {
        // Special bank questions route
        path: "/stations/part-2-video-bank/:id",
        name: "VideoStationPage",
        component: () =>
          import(
            /* webpackChunkName: "VideoStationPage" */ "@/views/SessionVideoBank.vue"
          ),
      },
      {
        // shows a single question - can select the bank
        path: "/questions/:bank/:id",
        name: "Question Page",
        component: Session,
      },
      // {
      //   // shows a single question - only for part 2
      //   path: "/questions/:id",
      //   name: "Question Page",
      //   component: Session,
      // },
      {
        path: "/admin",
        name: "AdminUsers",
        meta: { requiresAdmin: true, requiredLevel: "Super Admin" },
        component: () =>
          import(/* webpackChunkName: "AdminUsers" */ "@/views/AdminUsers.vue"),
      },
      {
        path: "/admin/explanations",
        name: "AdminExplanations",
        meta: { requiresAdmin: true, requiredLevel: "Question Writer" },
        component: () =>
          import(
            /* webpackChunkName: "AdminExplanations" */ "@/views/AdminExplanations.vue"
          ),
      },
      {
        path: "/admin/explanations/view/:id",
        name: "AdminExplanationsView",
        meta: { requiresAdmin: true, requiredLevel: "Question Writer" },
        component: () =>
          import(
            /* webpackChunkName: "AdminExplanationsView" */ "@/views/AdminExplanationsView.vue"
          ),
      },
      {
        path: "/admin/explanations/drafts/edit/:id",
        name: "AdminExplanationDraftEdit",
        meta: { requiresAdmin: true, requiredLevel: "Question Writer" },
        component: () =>
          import(
            /* webpackChunkName: "AdminExplanationsEdit" */ "@/views/AdminExplanationsEdit.vue"
          ),
      },
      {
        path: "/admin/explanations/drafts/new",
        name: "AdminExplanationDraftNew",
        meta: { requiresAdmin: true, requiredLevel: "Question Writer" },
        component: () =>
          import(
            /* webpackChunkName: "AdminExplanationsEdit" */ "@/views/AdminExplanationsEdit.vue"
          ),
      },
      {
        path: "/admin/explanations/edit/:id",
        name: "AdminExplanationsEdit",
        meta: { requiresAdmin: true, requiredLevel: "Question Writer" },
        component: () =>
          import(
            /* webpackChunkName: "AdminExplanationsEdit" */ "@/views/AdminExplanationsEdit.vue"
          ),
      },
      {
        path: "/admin/explanations/new",
        name: "AdminExplanationsNew",
        meta: { requiresAdmin: true, requiredLevel: "Question Writer" },
        component: () =>
          import(
            /* webpackChunkName: "AdminExplanationsNew" */ "@/views/AdminExplanationsEdit.vue"
          ),
      },
      {
        path: "/admin/content",
        name: "AdminContentSelection",
        meta: { requiresAdmin: true, requiredLevel: "Question Writer" },
        component: () =>
          import(
            /* webpackChunkName: "AdminContentSelection" */ "@/views/AdminContentSelection.vue"
          ),
      },
      {
        path: "/admin/videos",
        name: "AdminVideos",
        meta: {
          requiresAdmin: true,
          requiredAnyAccess: [{ manageVideos: true }],
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminVideos" */ "@/views/AdminVideos.vue"
          ),
      },
      {
        path: "/admin/flashcards/content",
        name: "AdminFlashcardsContent",
        meta: {
          requiresAdmin: true,
          requiredAnyBank: [
            "flashcard-de",
            "flashcard-mla",
            "flashcard-frcophth-part2",
          ],
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminFlashcardsContent" */ "@/views/AdminFlashcardsContent.vue"
          ),
      },
      {
        path: "/admin/flashcards/imports/:jobId",
        name: "AdminFlashcardImportJob",
        meta: {
          requiresAdmin: true,
          requiredAnyBank: [
            "flashcard-de",
            "flashcard-mla",
            "flashcard-frcophth-part2",
          ],
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminFlashcardImportJob" */ "@/views/AdminFlashcardImportJob.vue"
          ),
      },
      {
        path: "/admin/flashcards/content/edit/:id",
        name: "AdminFlashcardEdit",
        meta: {
          requiresAdmin: true,
          requiredAnyBank: [
            "flashcard-de",
            "flashcard-mla",
            "flashcard-frcophth-part2",
          ],
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminFlashcardEdit" */ "@/views/AdminFlashcardEdit.vue"
          ),
      },
      {
        path: "/admin/flashcards/content/visual-edit/:id",
        name: "AdminFlashcardVisualEdit",
        meta: {
          requiresAdmin: true,
          requiredAnyBank: [
            "flashcard-de",
            "flashcard-mla",
            "flashcard-frcophth-part2",
          ],
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminFlashcardVisualEdit" */ "@/views/AdminFlashcardVisualEdit.vue"
          ),
      },
      {
        path: "/admin/:bank/content",
        name: "AdminContent",
        meta: {
          requiresAdmin: true,
          requiredBank: (to) => to.params.bank,
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminContent" */ "@/views/AdminContent.vue"
          ),
      },
      {
        path: "/admin/interview-bank/content/edit/:id",
        name: "AdminInterviewStationContentEdit",
        meta: {
          requiresAdmin: true,
          requiredBank: "interview-bank",
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminInterviewStationContentEdit" */ "@/views/AdminInterviewStationContentEdit.vue"
          ),
      },
      {
        path: "/admin/interview-bank/content/visual-edit/:id",
        name: "AdminInterviewStationVisualEdit",
        meta: {
          requiresAdmin: true,
          requiredBank: "interview-bank",
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminInterviewStationVisualEdit" */ "@/views/AdminInterviewStationVisualEdit.vue"
          ),
      },
      {
        path: "/admin/part-2-video-bank/content/visual-edit/:id",
        name: "AdminVideoBankStationVisualEdit",
        meta: {
          requiresAdmin: true,
          requiredBank: "part-2-video-bank",
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminVideoBankStationVisualEdit" */ "@/views/AdminVideoBankStationVisualEdit.vue"
          ),
      },
      {
        path: "/admin/part-2-video-bank/content/edit/:id",
        name: "AdminVideoBankStationContentEdit",
        meta: {
          requiresAdmin: true,
          requiredBank: "part-2-video-bank",
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminVideoBankStationContentEdit" */ "@/views/AdminVideoBankStationContentEdit.vue"
          ),
      },
      {
        path: "/admin/:bank/content/question-writer",
        name: "AdminQuestionWriter",
        meta: {
          requiresAdmin: true,
          requiredBank: (to) => to.params.bank,
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminQuestionWriter" */ "@/views/AdminQuestionWriter.vue"
          ),
      },
      {
        path: "/admin/:bank/content/question-writer/knowledge-base",
        name: "AdminQuestionWriterKnowledgeBase",
        meta: {
          requiresAdmin: true,
          requiredBank: (to) => to.params.bank,
          requiredLevel: "Super Admin",
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminQuestionWriterKnowledgeBase" */ "@/views/AdminQuestionWriterKnowledgeBase.vue"
          ),
      },
      {
        path: "/admin/:bank/content/question-writer/:sessionId",
        name: "AdminQuestionWriterSession",
        meta: {
          requiresAdmin: true,
          requiredBank: (to) => to.params.bank,
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminQuestionWriterSession" */ "@/views/AdminQuestionWriterSession.vue"
          ),
      },
      {
        path: "/admin/:bank/content/visual-edit/:id",
        name: "AdminQuestionVisualEdit",
        meta: {
          requiresAdmin: true,
          requiredBank: (to) => to.params.bank,
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminQuestionVisualEdit" */ "@/views/AdminQuestionVisualEdit.vue"
          ),
      },
      {
        path: "/admin/:bank/content/edit/:id",
        name: "AdminContentEdit",
        meta: {
          requiresAdmin: true,
          requiredBank: (to) => to.params.bank,
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminContentEdit" */ "@/views/AdminContentEdit.vue"
          ),
      },
      {
        path: "/admin/manage-admins",
        name: "AdminManageAdmins",
        meta: { requiresAdmin: true, requiredLevel: "Super Admin" },
        component: () =>
          import(
            /* webpackChunkName: "AdminManageAdmins" */ "@/views/AdminManageAdmins.vue"
          ),
      },
      {
        path: "/admin/announcements",
        name: "AnnouncementSettings",
        meta: { requiresAdmin: true, requiredLevel: "Question Writer" },
        component: () =>
          import(
            /* webpackChunkName: "AnnouncementSettings" */ "@/views/AnnouncementSettings.vue"
          ),
      },
      {
        path: "/admin/ai-settings",
        name: "AiSettings",
        meta: { requiresAdmin: true, requiredLevel: "Super Admin" },
        component: () =>
          import(/* webpackChunkName: "AiSettings" */ "@/views/AiSettings.vue"),
      },
      {
        path: "/admin/ai-voice-analytics",
        name: "AdminAiVoiceAnalytics",
        meta: { requiresAdmin: true, requiredLevel: "Super Admin" },
        component: () =>
          import(
            /* webpackChunkName: "AdminAiVoiceAnalytics" */ "@/views/AdminAiVoiceAnalytics.vue"
          ),
      },
      {
        path: "/admin/portfolio-settings",
        name: "AdminPortfolioSettings",
        meta: {
          requiresAdmin: true,
          requiredAnyAccess: [
            { level: "Super Admin" },
            { bank: "st1-portfolio" },
          ],
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminPortfolioSettings" */ "@/views/AdminPortfolioSettings.vue"
          ),
      },
      {
        path: "/admin/pub2",
        name: "AdminPub2Settings",
        meta: {
          requiresAdmin: true,
          requiredAnyAccess: [
            { level: "Super Admin" },
            { bank: "pub2" },
          ],
        },
        component: () =>
          import(
            /* webpackChunkName: "AdminPub2Settings" */ "@/views/AdminPub2Settings.vue"
          ),
      },

      // Knowledge Base routes
      {
        path: "/knowledge",
        name: "Knowledge",
        component: () =>
          import(/* webpackChunkName: "Knowledge" */ "@/views/Knowledge.vue"),
      },
      {
        path: "/knowledge/flashcards",
        name: "FlashCardsBrowse",
        component: () =>
          import(
            /* webpackChunkName: "FlashCardsBrowse" */ "@/views/FlashCardsBrowse.vue"
          ),
      },
      {
        path: "/knowledge/:bank",
        name: "KnowledgeBank",
        component: () =>
          import(
            /* webpackChunkName: "KnowledgeBank" */ "@/views/KnowledgeBank.vue"
          ),
      },

      // terms and privacy accessible by all
      {
        path: "/terms",
        name: "Terms",
        component: Terms,
      },
      {
        path: "/privacy",
        name: "Privacy",
        component: Privacy,
      },
      {
        path: "/mcp/authorize",
        name: "McpAuthorize",
        meta: { requiresAdmin: true, requiredLevel: "Super Admin" },
        component: () =>
          import(
            /* webpackChunkName: "McpAuthorize" */ "@/views/McpAuthorize.vue"
          ),
      },
      {
        path: "/flashcards/session",
        name: "FlashCardsSession",
        component: () =>
          import(
            /* webpackChunkName: "FlashCardsSession" */ "@/views/FlashCards.vue"
          ),
      },

      {
        path: "/flashcards/:id",
        name: "FlashCardView",
        component: () =>
          import(
            /* webpackChunkName: "FlashCardsSingle" */ "@/views/FlashCards.vue"
          ),
      },
      {
        path: "/flashcards",
        name: "FlashCards",
        component: () =>
          import(
            /* webpackChunkName: "FlashCards" */ "@/views/FlashCardsHome.vue"
          ),
      },
      // 404
      {
        path: "/:pathMatch(.*)*",
        name: "NotFound",
        component: () =>
          import(/* webpackChunkName: "404" */ "@/views/404.vue"),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return { top: 0 };
  },
});

installRouteProfiler({ router });
installPerformanceTelemetry({ router, functions });

// navigation guards
// onboarding route logic happens in Home ('/)
router.beforeEach(async (to) => {
  if (to.name === "Terms" || to.name === "Privacy") {
    return true;
  }

  const user = await getCurrentUser();
  if (user) {
    document.cookie = "logged-in-ea=true;domain=.eye.academy;path=/";

    if (to.name === "Login") {
      return "/";
    }

    if (
      !user.emailVerified &&
      !["Verify", "ChangePassword"].includes(to.name)
    ) {
      return "/verify";
    }

    if (to.meta.requiresAdmin) {
      const adminAccessStore = useAdminAccessStore();
      const alertStore = useAlertStore();

      try {
        await adminAccessStore.ensureLoaded(user, {
          maxAgeMs: ADMIN_ACCESS_CACHE_TTL_MS,
        });
      } catch (error) {
        console.error("Failed to load admin access:", error);
        alertStore.showAlert("Failed to verify admin access", "error");
        return "/";
      }

      if (!adminAccessStore.isAdmin) {
        alertStore.showAlert("Unauthorised access", "error");
        return "/";
      }

      if (
        to.meta.requiredLevel &&
        !adminAccessStore.hasRequiredLevel(to.meta.requiredLevel)
      ) {
        alertStore.showAlert("Unauthorised access", "error");
        return "/";
      }

      const requiredBank =
        typeof to.meta.requiredBank === "function"
          ? to.meta.requiredBank(to)
          : to.meta.requiredBank;

      if (requiredBank && !adminAccessStore.canAccessBank(requiredBank)) {
        alertStore.showAlert("Unauthorised access", "error");
        return "/";
      }

      if (
        Array.isArray(to.meta.requiredAnyBank) &&
        !adminAccessStore.canAccessAnyBank(to.meta.requiredAnyBank)
      ) {
        alertStore.showAlert("Unauthorised access", "error");
        return "/";
      }

      if (
        Array.isArray(to.meta.requiredAnyAccess) &&
        !adminAccessStore.hasAnyAccess(to.meta.requiredAnyAccess)
      ) {
        alertStore.showAlert("Unauthorised access", "error");
        return "/";
      }
    }

    return true;
  }

  if (to.name === "ChangePassword" || to.name === "SetupAccount") {
    return true;
  }

  if (to.name !== "Login") {
    return {
      name: "Login",
      query: to.name === "McpAuthorize" ? {redirect: to.fullPath} : {},
    };
  }

  return true;
});

export default router;
